// .blank // WAREHOUSE_RUN
// Silent, canvas-based flappy-style game shown on the 404 and password pages.
// Self-initializes if it finds #warehouseGameRoot in the DOM.

const FREE_SHIP_CODE = "FLAPPYFREE35";
const UNLOCK_SCORE = 35;
const STORAGE_CODE_KEY = "blank_warehouseFreeShipCode";
const STORAGE_HIGHSCORE_KEY = "blank_warehouseHighScore";

const GRAVITY = 0.5;
const FLAP_VELOCITY = -8.2;
const MAX_FALL_ROTATION = 55;
const MAX_RISE_ROTATION = -22;
const BOX_W = 44;
const BOX_H = 38;
const OBSTACLE_W = 76;
const BASE_GAP = 178;
const MIN_GAP = 128;
const BASE_SPEED = 2.7;
const MAX_SPEED = 6.5;
const SPAWN_GAP_PX = 300;

const OBSTACLE_TYPES = ["rack", "conveyor", "forklift"];

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

function readHighScore() {
  try {
    return parseInt(localStorage.getItem(STORAGE_HIGHSCORE_KEY), 10) || 0;
  } catch (e) {
    return 0;
  }
}

function saveHighScore(score) {
  try {
    const current = readHighScore();
    if (score > current) localStorage.setItem(STORAGE_HIGHSCORE_KEY, String(score));
  } catch (e) {}
}

function persistFreeShipCode() {
  try {
    if (!localStorage.getItem(STORAGE_CODE_KEY)) {
      localStorage.setItem(STORAGE_CODE_KEY, FREE_SHIP_CODE);
    }
  } catch (e) {}
}

function readFreeShipCode() {
  try {
    return localStorage.getItem(STORAGE_CODE_KEY);
  } catch (e) {
    return null;
  }
}

class WarehouseGame {
  constructor(root) {
    this.root = root;
    this.canvas = root.querySelector("#warehouseGameCanvas");
    this.ctx = this.canvas.getContext("2d");
    this.homeUrl = root.dataset.homeUrl || "/";

    this.hud = root.querySelector("#wgHud");
    this.scoreEl = root.querySelector("#wgScore");
    this.targetEl = root.querySelector("#wgTarget");
    this.startHint = root.querySelector("#wgStartHint");

    this.choiceScreen = root.querySelector("#wgChoiceScreen");
    this.choiceCodeEl = root.querySelector("#wgChoiceCode");
    this.copyGoHomeBtn = root.querySelector("#wgCopyGoHome");
    this.keepPlayingBtn = root.querySelector("#wgKeepPlaying");

    this.gameOverScreen = root.querySelector("#wgGameOverScreen");
    this.maxScoreEl = root.querySelector("#wgMaxScore");
    this.gameOverCodeSection = root.querySelector("#wgGameOverCodeSection");
    this.gameOverLockedSection = root.querySelector("#wgGameOverLockedSection");
    this.copyCodeBtn = root.querySelector("#wgCopyCode");
    this.replayBtn = root.querySelector("#wgReplay");
    this.goToStoreBtn = root.querySelector("#wgGoToStore");

    this.state = "idle"; // idle | running | unboxing | choice | gameover
    this.score = 0;
    this.speed = BASE_SPEED;
    this.endless = false;
    this.obstacles = [];
    this.lastSpawnX = 0;
    this.unboxProgress = 0;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);

    this.resize = this.resize.bind(this);
    this.loop = this.loop.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onKey = this.onKey.bind(this);

    this.resize();
    window.addEventListener("resize", this.resize);
    this.canvas.addEventListener("pointerdown", this.onInput);
    window.addEventListener("keydown", this.onKey);

    this.copyGoHomeBtn?.addEventListener("click", () => this.copyCodeAndGoHome());
    this.keepPlayingBtn?.addEventListener("click", () => this.enterEndlessMode());
    this.copyCodeBtn?.addEventListener("click", () => this.copyCode());
    this.replayBtn?.addEventListener("click", () => this.reset());
    this.goToStoreBtn?.addEventListener("click", () => {
      window.location.href = this.homeUrl;
    });

    this.resetBox();
    requestAnimationFrame(this.loop);
  }

  resize() {
    const rect = this.root.getBoundingClientRect();
    this.width = rect.width;
    this.height = rect.height;
    this.canvas.width = this.width * this.dpr;
    this.canvas.height = this.height * this.dpr;
    this.canvas.style.width = this.width + "px";
    this.canvas.style.height = this.height + "px";
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  resetBox() {
    this.boxX = Math.max(70, this.width * 0.24);
    this.boxY = this.height / 2;
    this.velocity = 0;
    this.rotation = 0;
  }

  reset() {
    this.state = "idle";
    this.score = 0;
    this.speed = BASE_SPEED;
    this.endless = false;
    this.obstacles = [];
    this.resetBox();
    this.gameOverScreen.hidden = true;
    this.choiceScreen.hidden = true;
    this.startHint.hidden = false;
    this.updateScoreUI();
  }

  onKey(e) {
    if (e.code === "Space") {
      e.preventDefault();
      this.handleFlap();
    }
  }

  onInput(e) {
    if (this.state === "choice" || this.state === "gameover") return;
    this.handleFlap();
  }

  handleFlap() {
    if (this.state === "idle") {
      this.state = "running";
      this.startHint.hidden = true;
    }
    if (this.state !== "running") return;
    this.velocity = FLAP_VELOCITY;
  }

  updateScoreUI() {
    this.scoreEl.textContent = this.score;
    this.targetEl.textContent = this.score >= UNLOCK_SCORE ? "ENDLESS" : `${UNLOCK_SCORE} / FREE SHIP`;
  }

  spawnObstacle() {
    const type = OBSTACLE_TYPES[Math.floor(Math.random() * OBSTACLE_TYPES.length)];
    const gap = clamp(BASE_GAP - this.score * 1.1, MIN_GAP, BASE_GAP);
    const margin = 60;
    const gapY = margin + Math.random() * (this.height - margin * 2 - gap);
    this.obstacles.push({
      x: this.width + OBSTACLE_W,
      type,
      gapY,
      gap,
      passed: false,
    });
  }

  update(dt) {
    if (this.state === "running") {
      this.velocity += GRAVITY * dt;
      this.boxY += this.velocity * dt;
      this.rotation = this.velocity < 0
        ? clamp(this.velocity * 3.2, MAX_RISE_ROTATION, 0)
        : clamp(this.velocity * 4.2, 0, MAX_FALL_ROTATION);

      if (this.boxY - BOX_H / 2 < 0 || this.boxY + BOX_H / 2 > this.height) {
        this.triggerGameOver();
        return;
      }

      if (this.obstacles.length === 0 || this.width - this.obstacles[this.obstacles.length - 1].x >= SPAWN_GAP_PX) {
        this.spawnObstacle();
      }

      for (const obs of this.obstacles) {
        obs.x -= this.speed * dt;

        if (!obs.passed && obs.x + OBSTACLE_W < this.boxX - BOX_W / 2) {
          obs.passed = true;
          this.score += 1;
          this.updateScoreUI();
          if (this.score === UNLOCK_SCORE && !this.endless) {
            this.triggerUnboxing();
            return;
          }
          if (this.endless) {
            this.speed = Math.min(MAX_SPEED, this.speed + 0.08);
          }
        }

        if (this.boxCollidesWith(obs)) {
          this.triggerGameOver();
          return;
        }
      }

      this.obstacles = this.obstacles.filter((o) => o.x > -OBSTACLE_W);
    } else if (this.state === "unboxing") {
      this.unboxProgress = Math.min(1, this.unboxProgress + dt / 40);
      if (this.unboxProgress >= 1 && !this._unboxDone) {
        this._unboxDone = true;
        this.showChoiceScreen();
      }
    }
  }

  boxCollidesWith(obs) {
    const pad = 6;
    const left = this.boxX - BOX_W / 2 + pad;
    const right = this.boxX + BOX_W / 2 - pad;
    const top = this.boxY - BOX_H / 2 + pad;
    const bottom = this.boxY + BOX_H / 2 - pad;

    if (right < obs.x || left > obs.x + OBSTACLE_W) return false;

    const gapTop = obs.gapY;
    const gapBottom = obs.gapY + obs.gap;
    return top < gapTop || bottom > gapBottom;
  }

  triggerUnboxing() {
    this.state = "unboxing";
    this.unboxProgress = 0;
    this._unboxDone = false;
    persistFreeShipCode();
  }

  showChoiceScreen() {
    this.state = "choice";
    this.choiceCodeEl.textContent = FREE_SHIP_CODE;
    this.choiceScreen.hidden = false;
  }

  enterEndlessMode() {
    this.choiceScreen.hidden = true;
    this.endless = true;
    this.state = "running";
    this.speed = BASE_SPEED + 1;
  }

  async copyCodeAndGoHome() {
    await this.copyToClipboard(FREE_SHIP_CODE);
    window.location.href = this.homeUrl;
  }

  async copyCode() {
    await this.copyToClipboard(FREE_SHIP_CODE);
    this.copyCodeBtn.textContent = "COPIED!";
    setTimeout(() => {
      this.copyCodeBtn.textContent = `COPY CODE: ${FREE_SHIP_CODE}`;
    }, 1600);
  }

  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {}
  }

  triggerGameOver() {
    this.state = "gameover";
    saveHighScore(this.score);
    const maxScore = Math.max(this.score, readHighScore());
    this.maxScoreEl.textContent = maxScore;

    const unlockedCode = readFreeShipCode();
    if (unlockedCode) {
      this.gameOverCodeSection.hidden = false;
      this.gameOverLockedSection.hidden = true;
      this.copyCodeBtn.textContent = `COPY CODE: ${unlockedCode}`;
    } else {
      this.gameOverCodeSection.hidden = true;
      this.gameOverLockedSection.hidden = false;
    }
    this.gameOverScreen.hidden = false;
  }

  draw() {
    const { ctx, width, height } = this;
    ctx.clearRect(0, 0, width, height);

    const bg = ctx.createLinearGradient(0, 0, 0, height);
    bg.addColorStop(0, "#ffffff");
    bg.addColorStop(1, "#f2f1ec");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, width, height);

    ctx.strokeStyle = "rgba(20,20,20,0.05)";
    ctx.lineWidth = 1;
    for (let y = 0; y < height; y += 28) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    ctx.fillStyle = "rgba(20,20,20,0.045)";
    for (let x = 0; x < width; x += 56) {
      ctx.fillRect(x, height - 6, 40, 6);
    }

    for (const obs of this.obstacles) this.drawObstacle(obs);

    if (this.state !== "idle") this.drawBox();
  }

  drawHazardStripe(x, y, w, h) {
    const { ctx } = this;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    const step = 14;
    ctx.fillStyle = "#efb400";
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = "#141414";
    for (let i = -h; i < w + h; i += step * 2) {
      ctx.beginPath();
      ctx.moveTo(x + i, y);
      ctx.lineTo(x + i + step, y);
      ctx.lineTo(x + i + step - h, y + h);
      ctx.lineTo(x + i - h, y + h);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  drawObstacle(obs) {
    const { ctx, width, height } = this;
    const gapTop = obs.gapY;
    const gapBottom = obs.gapY + obs.gap;

    if (obs.type === "rack") this.drawRack(obs, gapTop, gapBottom);
    else if (obs.type === "conveyor") this.drawConveyor(obs, gapTop, gapBottom);
    else this.drawForklift(obs, gapTop, gapBottom);
  }

  // A small kraft shipping box -- the same visual language as the player,
  // reused to fill obstacles so they read as real box stacks, not abstract shapes.
  drawShippingBox(cx, cy, w, h, tone) {
    const { ctx } = this;
    const tones = ["#c19a6b", "#b58a54", "#cba173"];
    ctx.fillStyle = tones[tone % tones.length];
    ctx.strokeStyle = "rgba(90,60,30,0.35)";
    ctx.lineWidth = 1;
    ctx.fillRect(cx - w / 2, cy - h / 2, w, h);
    ctx.strokeRect(cx - w / 2, cy - h / 2, w, h);
    ctx.fillStyle = "rgba(216,195,154,0.85)";
    ctx.fillRect(cx - w / 2, cy - 1.5, w, 3);
    ctx.beginPath();
    ctx.moveTo(cx, cy - h / 2);
    ctx.lineTo(cx, cy + h / 2);
    ctx.strokeStyle = "rgba(90,60,30,0.2)";
    ctx.stroke();
  }

  // Fills a vertical band with a stacked grid of shipping boxes, biased
  // toward the gap edge so the stack reads as "loaded right up to the opening".
  drawBoxStack(x, top, bottom, edge) {
    const boxW = 30, boxH = 24, gutter = 3;
    const cols = Math.floor(OBSTACLE_W / (boxW + gutter));
    const usableW = cols * (boxW + gutter) - gutter;
    const startX = x + (OBSTACLE_W - usableW) / 2 + boxW / 2;
    let seed = 0;

    if (edge === "top") {
      for (let y = bottom - boxH / 2; y > top; y -= boxH + gutter) {
        for (let c = 0; c < cols; c++) {
          this.drawShippingBox(startX + c * (boxW + gutter), y, boxW, boxH, seed++);
        }
      }
    } else {
      for (let y = top + boxH / 2; y < bottom; y += boxH + gutter) {
        for (let c = 0; c < cols; c++) {
          this.drawShippingBox(startX + c * (boxW + gutter), y, boxW, boxH, seed++);
        }
      }
    }
  }

  drawRack(obs, gapTop, gapBottom) {
    const { ctx, height } = this;
    const x = obs.x;

    ctx.fillStyle = "#e2e0d8";
    ctx.fillRect(x, 0, OBSTACLE_W, gapTop);
    ctx.fillStyle = "#dddad0";
    ctx.fillRect(x, gapBottom, OBSTACLE_W, height - gapBottom);

    ctx.strokeStyle = "rgba(20,20,20,0.25)";
    ctx.lineWidth = 3;
    ctx.strokeRect(x + 4, 4, OBSTACLE_W - 8, Math.max(0, gapTop - 8));
    ctx.strokeRect(x + 4, gapBottom + 4, OBSTACLE_W - 8, Math.max(0, height - gapBottom - 8));

    this.drawBoxStack(x, 8, gapTop - 12, "top");
    this.drawBoxStack(x, gapBottom + 12, height - 8, "bottom");

    this.drawHazardStripe(x, gapTop - 10, OBSTACLE_W, 10);
    this.drawHazardStripe(x, gapBottom, OBSTACLE_W, 10);
  }

  drawConveyor(obs, gapTop, gapBottom) {
    const { ctx, height } = this;
    const x = obs.x;

    ctx.fillStyle = "#48484f";
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x + OBSTACLE_W, 0);
    ctx.lineTo(x + OBSTACLE_W * 0.65, gapTop - 30);
    ctx.lineTo(x + OBSTACLE_W * 0.35, gapTop - 30);
    ctx.closePath();
    ctx.fill();
    this.drawBoxStack(x, 0, gapTop - 34, "top");

    ctx.fillStyle = "#3a3a40";
    ctx.fillRect(x, gapBottom + 30, OBSTACLE_W, height - gapBottom - 30);
    ctx.fillStyle = "#7a7a82";
    for (let cx = x + 12; cx < x + OBSTACLE_W - 6; cx += 18) {
      ctx.beginPath();
      ctx.arc(cx, gapBottom + 40, 6, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = "#54545c";
    ctx.lineWidth = 3;
    const offset = (obs.x * 0.6) % 16;
    for (let i = -16; i < OBSTACLE_W + 16; i += 16) {
      ctx.beginPath();
      ctx.moveTo(x + i + offset, gapBottom + 54);
      ctx.lineTo(x + i + offset + 8, gapBottom + 62);
      ctx.stroke();
    }
    this.drawBoxStack(x, gapBottom + 6, gapBottom + 30, "bottom");
  }

  drawForklift(obs, gapTop, gapBottom) {
    const { ctx, height } = this;
    const x = obs.x;
    const cx = x + OBSTACLE_W / 2;

    ctx.fillStyle = "#2a2a2e";
    ctx.fillRect(x, 0, OBSTACLE_W, 18);

    ctx.fillStyle = "#f2c230";
    ctx.fillRect(cx - 12, 18, 24, Math.max(0, gapTop - 18 - 20));
    ctx.fillStyle = "#8a8a90";
    ctx.fillRect(cx - 5, gapTop - 20, 10, 20);
    ctx.strokeStyle = "#c79a1f";
    ctx.lineWidth = 2;
    for (let y = 22; y < gapTop - 22; y += 12) {
      ctx.beginPath();
      ctx.moveTo(cx - 12, y);
      ctx.lineTo(cx + 12, y);
      ctx.stroke();
    }

    ctx.fillStyle = "#f2c230";
    ctx.fillRect(x + 10, height - 26, OBSTACLE_W - 20, 26);
    const forkTop = gapBottom + 34;
    ctx.fillStyle = "#c7c9d1";
    [x + 20, x + OBSTACLE_W / 2, x + OBSTACLE_W - 20].forEach((fx) => {
      ctx.beginPath();
      ctx.moveTo(fx - 5, height - 26);
      ctx.lineTo(fx + 5, height - 26);
      ctx.lineTo(fx + 5, forkTop + 10);
      ctx.lineTo(fx, forkTop);
      ctx.lineTo(fx - 5, forkTop + 10);
      ctx.closePath();
      ctx.fill();
    });
    this.drawBoxStack(x, gapBottom + 6, forkTop + 8, "bottom");
  }

  drawBox() {
    const { ctx } = this;
    ctx.save();
    ctx.translate(this.boxX, this.boxY);
    ctx.rotate((this.rotation * Math.PI) / 180);

    const w = BOX_W, h = BOX_H;

    ctx.fillStyle = "#c19a6b";
    ctx.strokeStyle = "#8f6b45";
    ctx.lineWidth = 1.5;
    this.roundRect(-w / 2, -h / 2, w, h, 4);
    ctx.fill();
    ctx.stroke();

    ctx.strokeStyle = "rgba(90,60,30,0.25)";
    ctx.lineWidth = 1;
    for (let i = 1; i < 4; i++) {
      const gy = -h / 2 + (h / 4) * i;
      ctx.beginPath();
      ctx.moveTo(-w / 2 + 3, gy);
      ctx.lineTo(w / 2 - 3, gy);
      ctx.stroke();
    }

    if (this.state !== "unboxing" && this.state !== "choice") {
      ctx.fillStyle = "#d8c39a";
      ctx.fillRect(-w / 2, -4, w, 8);
      ctx.strokeStyle = "#b9a074";
      ctx.strokeRect(-w / 2, -4, w, 8);

      ctx.fillStyle = "#111111";
      ctx.font = "bold 8px 'Space Grotesk', sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(".blank", 0, -h / 2 + 8);

      ctx.fillStyle = "#fafaf8";
      ctx.fillRect(-10, h / 2 - 13, 20, 10);
      ctx.strokeStyle = "#ccc";
      ctx.strokeRect(-10, h / 2 - 13, 20, 10);
      ctx.fillStyle = "#141414";
      let bx = -8;
      while (bx < 8) {
        const bw = Math.random() > 0.5 ? 1 : 2;
        ctx.fillRect(bx, h / 2 - 12, bw, 8);
        bx += bw + 1.5;
      }
    } else {
      this.drawUnboxing(w, h);
    }

    ctx.restore();
  }

  drawUnboxing(w, h) {
    const { ctx } = this;
    const p = this.unboxProgress;
    const flapSwing = Math.min(1, p * 1.6);

    ctx.fillStyle = `rgba(216,195,154,${Math.max(0, 1 - p * 2)})`;
    ctx.fillRect(-w / 2, -4, w, 8);

    ctx.fillStyle = "#c19a6b";
    ctx.strokeStyle = "#8f6b45";
    ctx.lineWidth = 1.5;

    ctx.save();
    ctx.translate(-w / 2, -h / 2);
    ctx.rotate((-flapSwing * 50 * Math.PI) / 180);
    ctx.fillRect(0, -14, w / 2, 14);
    ctx.strokeRect(0, -14, w / 2, 14);
    ctx.restore();

    ctx.save();
    ctx.translate(w / 2, -h / 2);
    ctx.rotate((flapSwing * 50 * Math.PI) / 180);
    ctx.fillRect(-w / 2, -14, w / 2, 14);
    ctx.strokeRect(-w / 2, -14, w / 2, 14);
    ctx.restore();

    if (p > 0.35) {
      const slideProgress = clamp((p - 0.35) / 0.5, 0, 1);
      const slipY = -h / 2 - slideProgress * 30;
      ctx.fillStyle = "#ffffff";
      ctx.strokeStyle = "#ddd";
      ctx.fillRect(-w / 2 + 4, slipY, w - 8, 26);
      ctx.strokeRect(-w / 2 + 4, slipY, w - 8, 26);
      if (slideProgress > 0.5) {
        ctx.fillStyle = "#111111";
        ctx.font = "bold 6.5px monospace";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(FREE_SHIP_CODE, 0, slipY + 13);
      }
    }
  }

  roundRect(x, y, w, h, r) {
    const { ctx } = this;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  loop(ts) {
    if (!this.lastTs) this.lastTs = ts;
    const dt = clamp((ts - this.lastTs) / 16.6667, 0, 2.5);
    this.lastTs = ts;

    this.update(dt);
    this.draw();

    requestAnimationFrame(this.loop);
  }
}

function init() {
  const root = document.getElementById("warehouseGameRoot");
  if (root) new WarehouseGame(root);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
